import { useRequestDone } from '@mentimeter/core-global-state';
import { MentiError } from '@mentimeter/error-utils/error';
import { getRequestFailureCause } from '@mentimeter/errors/getRequestFailureCause';
import { type Regions } from '@mentimeter/http-clients';
import { getRegionBySeriesId } from '@mentimeter/region';
import { useCallback } from 'react';
import { processLegacySeriesData } from './process-legacy-series-data';
import { makeRequest } from './request-wrapper/make-request';
import { fromSnakeKeys } from './snake-keys';

interface GetSeriesRequest {
  region: 'eu' | 'us';
  userAuth: string;
  seriesId: string;
}

type SeriesFetcher = (args: GetSeriesRequest) => Request;

const fetchSeriesOrSlideDeck = async <TSeriesData extends object>(
  seriesId: string,
  coreApiFetcher: SeriesFetcher,
  region: Regions,
): Promise<TSeriesData> => {
  let response: Response;
  try {
    response = await fetch(makeRequest(coreApiFetcher, { region, seriesId }));
  } catch (ex) {
    const cause = getRequestFailureCause(ex);
    throw new MentiError('Fetch series request failed', {
      feature: 'devops',
      cause,
    });
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch series: ${response.status}`);
  }

  let json;
  try {
    json = await response.json();
  } catch (ex) {
    throw new Error('Unexpected series response', { cause: ex });
  }
  if (json.slide_deck) {
    // Object was provided in snake_case by the API.
    const camelCaseSlideDeck: TSeriesData = fromSnakeKeys(json.slide_deck);
    return camelCaseSlideDeck;
  } else if (json.slideDeck) {
    // Object was provided in camelCase by the API.
    const camelCaseSlideDeck = json.slideDeck;
    return {
      ...camelCaseSlideDeck,
      slideDeckSegmentations: camelCaseSlideDeck.segmentations,
    } as TSeriesData;
  }

  const series = processLegacySeriesData(json.series);
  return series as TSeriesData;
};

export const useSlideDeckFetcher = <TSeriesData extends object>(
  seriesId: string,
  coreApiFetcher: SeriesFetcher,
) => {
  const requestDone = useRequestDone();

  return useCallback(async () => {
    const region = getRegionBySeriesId(seriesId);

    const result = await fetchSeriesOrSlideDeck<TSeriesData>(
      seriesId,
      coreApiFetcher,
      region,
    );
    requestDone();
    return result;
  }, [coreApiFetcher, requestDone, seriesId]);
};

export const useSlideFetcher = <TQuestionData>(
  seriesId: string,
  coreApiFetcher: SeriesFetcher,
) => {
  const requestDone = useRequestDone();

  return useCallback(async () => {
    const region = getRegionBySeriesId(seriesId);
    const result = await fetchSeriesOrSlideDeck(
      seriesId,
      coreApiFetcher,
      region,
    );
    const seriesOrSlideDeck = result ?? {};
    requestDone();

    if ('slides' in seriesOrSlideDeck) {
      return seriesOrSlideDeck.slides as TQuestionData;
    }

    if ('questions' in seriesOrSlideDeck) {
      return seriesOrSlideDeck.questions as TQuestionData;
    }

    throw new MentiError(
      `Unexpected response structure: ${JSON.stringify(seriesOrSlideDeck)}`,
      {
        feature: 'compatibility-layer',
      },
    );
  }, [coreApiFetcher, requestDone, seriesId]);
};
